import merge from "lodash/merge";
import { createI18n } from "vue-i18n";
import { axiosFactory } from "@/tools/axios-factory";

type MessageMap = Record<string, any>;

const isObject = (value: unknown): value is MessageMap => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const sanitizeApiMessages = (value: unknown): unknown => {
  if (typeof value === "string") {
    return /^Phrases\./i.test(value.trim()) ? undefined : value;
  }

  if (!isObject(value)) {
    return value;
  }

  const sanitizedEntries = Object.entries(value)
    .map(([key, nestedValue]) => [key, sanitizeApiMessages(nestedValue)] as const)
    .filter(([, nestedValue]) => nestedValue !== undefined);

  return Object.fromEntries(sanitizedEntries);
};

interface Language {
  key: string;
  value: string;
}

export const fallbackMessages: MessageMap = {
  Phrases: {
    Participation: {
      ProgramPriceEstimate: {
        EstimatedProcessingFee: "Estimated processing fee",
        NetVenueAmount: "Net amount your club receives",
        AmountShownDuringRegistration: "Amount shown to customer.",
        AmountPaidByClub: "Amount paid by the club.",
        ClubPayout: "Club payout",
        AfterProcessingFee: "After processing fee",
        ParticipantPays: "The customer pays {advertisedPrice}.",
        ClubReceives:
          "Your club receives {netVenueAmount} after the {estimatedProcessingFee} processing fee.",
        ExistingPricesParticipantFacing:
          "Existing programme prices are participant-facing amounts and are not adjusted automatically.",
        ProgramPriceTooltip:
          "Please note: The total Program Price you enter is the final amount the customer will pay. Stripe processing fees will be deducted from this amount, so please factor these fees into your program price.",
        Unavailable:
          "The processing fee estimate is temporarily unavailable. You can still save the {program}.",
      },
    },
  },
};

export const i18n = createI18n<MessageMap, string, false>({
  legacy: false,
  globalInjection: true,
  locale: "en",
  fallbackLocale: "en",
  messages: {
    en: fallbackMessages,
  },
});

export async function loadLocaleMessages(
  languages: Language[],
  preferredLanguage: string
): Promise<void> {
  const client = new axiosFactory().withoutToken();

  await Promise.all(languages.map(async (language) => {
    try {
      const response = await client.get("/api/Language/Get", {
        params: { locale: language.key },
      });
      const result = response.data.data;

      if (!result?.messages) {
        throw new Error(response.data.errorMessage ?? "Failed to load language pack");
      }

      i18n.global.setLocaleMessage(
        result.locale,
        merge({}, fallbackMessages, sanitizeApiMessages(JSON.parse(result.messages)))
      );
    } catch (error) {
      console.error(`Unable to load Participation language resources for "${language.key}".`, error);
    }
  }));

  i18n.global.locale.value = preferredLanguage;
}
